import React, { useState } from "react";

import { useFormContext, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ErrorMessage, RequiredMark } from "@components/inputs/shared";
import classNames from "classnames";

import { PopoverMessage } from ".";
import { PasswordInputProps } from "./types";

export default function PasswordInput(props: PasswordInputProps) {
	const {
		id,
		label,
		labelClassName,
		inputClassName = undefined,
		name,
		className,
		errorMessage,
		required = false,
		placeholder,
		children,
		asterisksClass,
		popoverMsg = null,
		disabled,
		validate,
		pattern,
		onChange,
		...rest
	} = props;
	const [showPopoverMsg, setShowPopoverMsg] = useState(false);
	const [showIcon, setShowIcon] = useState(true);
	const [pwdIcon, setPwdIcon] = useState(false);
	const [pwdType, setPwdType] = useState("password");

	const { t } = useTranslation();
	const { register } = useFormContext();

	const { errors } = useFormState();
	const error = errors?.name;

	const field = register(name, {
		required: {
			value: required,
			message: errorMessage || "COMMON.REQUIRED_FIELD",
		},
		disabled: disabled,
		onChange,
		validate,
		pattern,
	});

	const labelClasses = classNames("label", labelClassName);

	const inputClass = classNames("field--icon__input", inputClassName, {
		"input input--base":
			inputClassName == undefined || inputClassName == null,
		"input-validation-error": error != undefined,
	});

	const iconClass = classNames("u-icon u-icon--sml", {
		"u-icon--view--off": !pwdIcon,
		"u-icon--view--on": pwdIcon,
	});

	const togglePwdType = () => {
		/* setTimeout hack for chrome browser on mobile */
		/* chrome moves cursor on start after focus */
		let inputRef = document.querySelector(
			`[name=${name}]`
		) as HTMLInputElement | null;

		inputRef?.focus();
		setTimeout(function () {
			setPwdIcon(!pwdIcon);
			if (pwdType == "password") {
				setPwdType("text");
			} else {
				setPwdType("password");
			}
			if (inputRef) {
				inputRef.selectionStart = inputRef.selectionEnd =
					inputRef.value.length;
			}
		}, 0);
	};

	const handleInput = (e: any) => {
		e.persist();
		if (e.target.value.length == 0) {
			setShowIcon(false);
		} else if (!showIcon) {
			setShowIcon(true);
		}
	};

	return (
		<>
			<div className={className}>
				<label
					htmlFor={id != null ? id : ""}
					className={labelClasses}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						setShowPopoverMsg(true);
					}}
				>
					{t(label)}
					<RequiredMark
						required={required}
						asterisksClass={asterisksClass}
					/>
					<PopoverMessage
						showPopoverMsg={showPopoverMsg}
						setShowPopoverMsg={setShowPopoverMsg}
						popoverMsg={popoverMsg}
					/>
				</label>

				<ErrorMessage name={name} />

				<div className="field--icon">
					<input
						{...field}
						{...rest}
						type={pwdType}
						id={id}
						className={inputClass}
						placeholder={t(placeholder)}
						onInput={handleInput}
					/>
					<div
						className="field--icon__symbol"
						onClick={togglePwdType}
					>
						{showIcon ? <i className={iconClass} /> : null}
					</div>
				</div>
				{children}
			</div>
		</>
	);
}
